import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight, lucideX } from '@ng-icons/lucide';

import { ZardButtonComponent } from '@solvace/zard/shared/components/button';
import { ZardDialogRef } from '@solvace/zard/shared/components/dialog/dialog-ref';
import { Z_MODAL_DATA } from '@solvace/zard/shared/components/dialog/dialog.service';

import type { SvzMediaCarouselItem } from '../../svz-media-carousel/interfaces/svz-media-carousel-item';
import { SvzMediaCarouselComponent } from '../../svz-media-carousel/svz-media-carousel.component';
import { SvzMediaCarouselModule } from '../../svz-media-carousel/svz-media-carousel.module';
import type { SvzImagesViewerConfig, SvzImagesViewerItem } from './svz-images-viewer.types';

@Component({
  selector: 'svz-images-viewer-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ZardButtonComponent, NgIcon, SvzMediaCarouselModule],
  templateUrl: './svz-images-viewer-modal.component.html',
  viewProviders: [provideIcons({ lucideX, lucideChevronLeft, lucideChevronRight })],
})
export class SvzImagesViewerModalComponent implements AfterViewInit {
  private readonly dialogRef = inject(ZardDialogRef<SvzImagesViewerModalComponent, void>);
  private readonly data = inject<{ imagesUrl: string | SvzImagesViewerItem[]; config?: SvzImagesViewerConfig }>(Z_MODAL_DATA);

  protected readonly mediasContainer = viewChild<ElementRef<HTMLElement>>('mediasContainer');
  protected readonly carousel = viewChild(SvzMediaCarouselComponent);

  protected config: SvzImagesViewerConfig = {
    fitContent: 'contain',
  };

  protected carouselItems: SvzMediaCarouselItem[] = [];
  protected carouselInitialItem: SvzMediaCarouselItem | undefined;
  protected carouselHeight = 0;

  constructor() {
    const imagesUrl: SvzImagesViewerItem[] = Array.isArray(this.data.imagesUrl) ? this.data.imagesUrl : this.data.imagesUrl ? [{ url: this.data.imagesUrl, type: 'image' }] : [];

    this.carouselItems = imagesUrl.map((object) => {
      const item: SvzMediaCarouselItem = {
        url: object.url,
        type: object.type,
      };
      if (object.current) {
        this.carouselInitialItem = item;
      }
      return item;
    });

    this.config = {
      ...this.config,
      ...(this.data.config ?? {}),
    };
  }

  ngAfterViewInit(): void {
    this.onWindowResize();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.carouselHeight = Math.min(typeof window !== 'undefined' ? window.innerHeight - 40 : 410, 410);
  }

  protected prevMediasPreview(): void {
    const el = this.mediasContainer()?.nativeElement;
    if (el) {
      el.scrollLeft -= 68;
    }
  }

  protected nextMediasPreview(): void {
    const el = this.mediasContainer()?.nativeElement;
    if (el) {
      el.scrollLeft += 68;
    }
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected get mediasContainerEl(): HTMLElement | undefined {
    return this.mediasContainer()?.nativeElement;
  }
}
